import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { CalendarEntry, FollowUpEntry, TaskEntry, WorkAssignmentEntry, CallLogEntry, CalendarItem, User } from './Calendar/CalendarTypes';
import { JournalService } from './Calendar/JournalService';
import { FollowUpService } from './Calendar/FollowUpService';
import { TaskService } from './Calendar/TaskService';
import { WorkAssignmentService } from './Calendar/WorkAssignmentService';
import { CallLogService } from './Calendar/CallLogService';
import { CalendarUtils } from './Calendar/CalendarUtils';



interface CalendarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Calendar: React.FC<CalendarProps> = ({ isOpen, onClose }) => {
  const { currentUser, userData, isAdmin, isManager } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [journal, setJournal] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endTime, setEndTime] = useState<string>('09:00');
  const [entryMode, setEntryMode] = useState<'journal' | 'workAssignment'>('journal');
  const [workDescription, setWorkDescription] = useState<string>('');
  const [workDueTime, setWorkDueTime] = useState<string>('17:00');
  const [journalEntries, setJournalEntries] = useState<CalendarEntry[]>([]);
  const [followupEntries, setFollowupEntries] = useState<FollowUpEntry[]>([]);
  const [taskEntries, setTaskEntries] = useState<TaskEntry[]>([]);
  const [workAssignmentEntries, setWorkAssignmentEntries] = useState<WorkAssignmentEntry[]>([]);
  const [callLogEntries, setCallLogEntries] = useState<CallLogEntry[]>([]);
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState({
    journals: false,
    followups: false,
    tasks: false,
    workAssignments: false,
    callLogs: false
  });
  const [companyId, setCompanyId] = useState<string>('');
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; entryId: string; entryUserId: string; entryJournal: string }>({
    isOpen: false,
    entryId: '',
    entryUserId: '',
    entryJournal: ''
  });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [markDoneModal, setMarkDoneModal] = useState<{ isOpen: boolean; followUpId: string; leadId: string; description: string }>({
    isOpen: false,
    followUpId: '',
    leadId: '',
    description: ''
  });
  const [isMarkingDone, setIsMarkingDone] = useState<boolean>(false);
  const [markTaskDoneModal, setMarkTaskDoneModal] = useState<{ isOpen: boolean; taskId: string; description: string }>({
    isOpen: false,
    taskId: '',
    description: ''
  });
  const [isMarkingTaskDone, setIsMarkingTaskDone] = useState<boolean>(false);
  const [markWorkDoneModal, setMarkWorkDoneModal] = useState<{ isOpen: boolean; workAssignmentId: string; description: string }>({
    isOpen: false,
    workAssignmentId: '',
    description: ''
  });
  const [isMarkingWorkDone, setIsMarkingWorkDone] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sidebarWidth, setSidebarWidth] = useState(450); // Default width
  const [isResizing, setIsResizing] = useState(false);
  const navigate = useNavigate();
  const [isAddEntryExpanded, setIsAddEntryExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Get company ID
  useEffect(() => {
    if (userData?.companyId) {
      setCompanyId(userData.companyId);
    }
  }, [userData]);

  // Set default selected user to current user
  useEffect(() => {
    if (currentUser?.uid) {
      setSelectedUserId(currentUser.uid);
    }
  }, [currentUser]);

  // Fetch users based on role
  useEffect(() => {
    const fetchUsers = async () => {
      if (!companyId || !currentUser) return;

      try {
        const usersRef = collection(db, 'users');
        let usersQuery;

        if (isAdmin) {
          // Admin can see all users in the company
          usersQuery = query(
            usersRef,
            where('companyId', '==', companyId)
          );
        } else if (isManager) {
          // Manager can see users under them + themselves
          usersQuery = query(
            usersRef,
            where('companyId', '==', companyId)
          );
        } else {
          // Regular user can only see themselves
          usersQuery = query(
            usersRef,
            where('companyId', '==', companyId),
            where('__name__', '==', currentUser.uid)
          );
        }

        const snapshot = await getDocs(usersQuery);
        const usersData: User[] = [];

        snapshot.forEach((doc) => {
          const user = doc.data();
          if (isAdmin) {
            // Admin sees all users
            usersData.push({
              id: doc.id,
              name: user.name || user.email,
              email: user.email,
              managerIds: user.managerIds,
              isAdmin: user.isAdmin
            });
          } else if (isManager) {
            // Manager sees users under them + themselves
            if (doc.id === currentUser.uid || (user.managerIds && user.managerIds.includes(currentUser.uid))) {
              usersData.push({
                id: doc.id,
                name: user.name || user.email,
                email: user.email,
                managerIds: user.managerIds,
                isAdmin: user.isAdmin
              });
            }
          } else {
            // Regular user sees only themselves
            usersData.push({
              id: doc.id,
              name: user.name || user.email,
              email: user.email,
              managerIds: user.managerIds,
              isAdmin: user.isAdmin
            });
          }
        });

        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, [companyId, currentUser, isAdmin, isManager]);

  // Fetch journal entries for selected user and date
  const fetchJournalEntries = async () => {
    if (!companyId || !selectedDate || !currentUser) return;

    setLoadingStates(prev => ({ ...prev, journals: true }));
    try {
      const entriesData = await JournalService.fetchJournalEntries(
        companyId,
        selectedDate,
        selectedUserId,
        currentUser.uid,
        isAdmin,
        isManager
      );

      setJournalEntries(entriesData);
      const times = JournalService.updateTimesBasedOnEntries(entriesData);
      setStartTime(times.startTime);
      setEndTime(times.endTime);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, journals: false }));
    }
  };

  // Fetch followup entries for selected user and date
  const fetchFollowUpEntries = async () => {
    if (!companyId || !selectedDate || !selectedUserId) return;

    setLoadingStates(prev => ({ ...prev, followups: true }));
    try {
      const followUpsData = await FollowUpService.fetchFollowUpEntries(
        companyId,
        selectedDate,
        selectedUserId
      );

      setFollowupEntries(followUpsData);
    } catch (error) {
      console.error('Error fetching followup entries:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, followups: false }));
    }
  };

  // Fetch task entries for selected user and date
  const fetchTaskEntries = async () => {
    if (!companyId || !selectedDate || !selectedUserId) return;

    setLoadingStates(prev => ({ ...prev, tasks: true }));
    try {
      const tasksData = await TaskService.fetchTaskEntries(
        companyId,
        selectedDate,
        selectedUserId
      );

      setTaskEntries(tasksData);
    } catch (error) {
      console.error('Error fetching task entries:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, tasks: false }));
    }
  };

  // Fetch work assignment entries for selected user and date
  const fetchWorkAssignmentEntries = async () => {
    if (!companyId || !selectedDate || !selectedUserId) return;

    setLoadingStates(prev => ({ ...prev, workAssignments: true }));
    try {
      const workAssignmentsData = await WorkAssignmentService.fetchWorkAssignmentEntries(
        companyId,
        selectedDate,
        selectedUserId
      );

      setWorkAssignmentEntries(workAssignmentsData);
    } catch (error) {
      console.error('Error fetching work assignment entries:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, workAssignments: false }));
    }
  };

  // Fetch call log entries for selected user and date
  const fetchCallLogEntries = async () => {
    if (!companyId || !selectedDate || !selectedUserId) return;

    setLoadingStates(prev => ({ ...prev, callLogs: true }));
    try {
      const callLogsData = await CallLogService.fetchCallLogEntries(
        companyId,
        selectedDate,
        selectedUserId
      );

      setCallLogEntries(callLogsData);
    } catch (error) {
      console.error('Error fetching call log entries:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, callLogs: false }));
    }
  };

  // Combine journal entries, followups, tasks, work assignments, and call logs
  const combineEntries = () => {
    // Only combine entries when all data is loaded to prevent items from popping up individually
    if (!isAnyDataLoading()) {
      const sorted = CalendarUtils.combineAndSortEntries(journalEntries, followupEntries, taskEntries, workAssignmentEntries, callLogEntries);
      setCalendarItems(sorted);
    }
  };

  // Fetch all entries for selected user and date
  const fetchAllEntries = async () => {
    await Promise.all([fetchJournalEntries(), fetchFollowUpEntries(), fetchTaskEntries(), fetchWorkAssignmentEntries(), fetchCallLogEntries()]);
  };

  // Refresh data function
  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchAllEntries();
      console.log('Calendar data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing calendar data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllEntries();
  }, [selectedDate, selectedUserId, companyId]);

  useEffect(() => {
    combineEntries();
  }, [journalEntries, followupEntries, taskEntries, workAssignmentEntries, callLogEntries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle sidebar resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 400;
      const maxWidth = 800;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Update start and end times based on existing entries
  const updateTimesBasedOnEntries = (entriesData: CalendarEntry[]) => {
    if (entriesData.length === 0) {
      // No entries - keep start time at 9:00 AM, end time at 9:00 AM
      setStartTime('09:00');
      setEndTime('09:00');
    } else {
      // Find the latest entry's end time
      const latestEntry = entriesData.reduce((latest, current) => {
        return current.createdAt > latest.createdAt ? current : latest;
      });

      // Set start time to the latest entry's end time
      setStartTime(latestEntry.endTime);

      // Set end time to current time
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
      setEndTime(currentTime);
    }
  };

  // Handle start time change with validation
  const handleStartTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);
    // If end time is less than start time, update end time to match start time
    if (endTime < newStartTime) {
      setEndTime(newStartTime);
    }
  };

  // Handle end time change with validation
  const handleEndTimeChange = (newEndTime: string) => {
    // Only allow end time if it's greater than or equal to start time
    if (newEndTime >= startTime) {
      setEndTime(newEndTime);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userData || !companyId) {
      alert('Missing required information');
      return;
    }

    setIsLoading(true);
    try {
      if (!currentUser) {
        alert('User not authenticated');
        return;
      }

      if (entryMode === 'journal') {
        await JournalService.addJournalEntry(
          companyId,
          currentUser.uid,
          (userData as any)?.name || (userData as any)?.email || 'Unknown',
          (userData as any)?.email || '',
          selectedDate,
          journal,
          startTime,
          endTime
        );

        setJournal('');
        setStartTime('09:00');
        setEndTime('17:00');
        console.log('Journal entry added successfully');
      } else {
        // Create work assignment
        const dueDateTime = new Date(selectedDate);
        const [hours, minutes] = workDueTime.split(':');
        dueDateTime.setHours(parseInt(hours), parseInt(minutes));

        await WorkAssignmentService.createWorkAssignment(
          companyId,
          selectedUserId, // Assign to selected user
          currentUser.uid, // Assigned by current user
          workDescription,
          dueDateTime
        );

        setWorkDescription('');
        setWorkDueTime('17:00');
        console.log('Work assignment created successfully');
      }

      // Refetch entries after adding
      fetchAllEntries();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Failed to add entry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (entryId: string, entryUserId: string, entryJournal: string) => {
    // Only allow deleting own entries unless admin
    if (!isAdmin && entryUserId !== currentUser?.uid) {
      alert('You can only delete your own entries');
      return;
    }

    setDeleteModal({
      isOpen: true,
      entryId,
      entryUserId,
      entryJournal
    });
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await JournalService.deleteJournalEntry(companyId, deleteModal.entryId);
      console.log('Entry deleted successfully');
      // Refetch entries after deleting
      fetchAllEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry');
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, entryId: '', entryUserId: '', entryJournal: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, entryId: '', entryUserId: '', entryJournal: '' });
  };

  const handleMarkAsDone = (followUpId: string, leadId: string, description: string) => {
    setMarkDoneModal({ isOpen: true, followUpId, leadId, description });
  };

  const confirmMarkAsDone = async () => {
    setIsMarkingDone(true);
    try {
      await FollowUpService.markFollowUpAsDone(companyId, markDoneModal.followUpId, markDoneModal.leadId);
      console.log('Follow-up marked as done successfully');
      // Refetch entries after marking as done
      fetchAllEntries();
    } catch (error) {
      console.error('Error marking follow-up as done:', error);
      alert('Failed to mark follow-up as done');
    } finally {
      setIsMarkingDone(false);
      setMarkDoneModal({ isOpen: false, followUpId: '', leadId: '', description: '' });
    }
  };

  const cancelMarkAsDone = () => {
    setMarkDoneModal({ isOpen: false, followUpId: '', leadId: '', description: '' });
  };

  const handleMarkTaskAsDone = (taskId: string, description: string) => {
    setMarkTaskDoneModal({ isOpen: true, taskId, description });
  };

  const confirmMarkTaskAsDone = async () => {
    setIsMarkingTaskDone(true);
    try {
      await TaskService.markTaskAsDone(companyId, markTaskDoneModal.taskId);
      console.log('Task marked as done successfully');
      // Refetch entries after marking as done
      fetchAllEntries();
    } catch (error) {
      console.error('Error marking task as done:', error);
      alert('Failed to mark task as done');
    } finally {
      setIsMarkingTaskDone(false);
      setMarkTaskDoneModal({ isOpen: false, taskId: '', description: '' });
    }
  };

  const cancelMarkTaskAsDone = () => {
    setMarkTaskDoneModal({ isOpen: false, taskId: '', description: '' });
  };

  const handleMarkWorkAsDone = (workAssignmentId: string, description: string) => {
    setMarkWorkDoneModal({ isOpen: true, workAssignmentId, description });
  };

  const confirmMarkWorkAsDone = async () => {
    setIsMarkingWorkDone(true);
    try {
      await WorkAssignmentService.markWorkAssignmentAsDone(companyId, markWorkDoneModal.workAssignmentId);
      console.log('Work assignment marked as done successfully');
      // Refetch entries after marking as done
      fetchAllEntries();
    } catch (error) {
      console.error('Error marking work assignment as done:', error);
      alert('Failed to mark work assignment as done');
    } finally {
      setIsMarkingWorkDone(false);
      setMarkWorkDoneModal({ isOpen: false, workAssignmentId: '', description: '' });
    }
  };

  const cancelMarkWorkAsDone = () => {
    setMarkWorkDoneModal({ isOpen: false, workAssignmentId: '', description: '' });
  };

  // Custom dropdown functions
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUser = users.find(user => user.id === selectedUserId);

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setSearchTerm('');
    }
  };

  // Convert 24-hour time to 12-hour AM/PM format
  const formatTime = (time: string): string => {
    return CalendarUtils.formatTime(time);
  };

  const canEditEntry = (entryUserId: string): boolean => {
    return CalendarUtils.canEditEntry(entryUserId, currentUser?.uid || '');
  };

  const canAddEntry = (): boolean => {
    return CalendarUtils.canAddEntry(selectedUserId, currentUser?.uid || '');
  };

  const canAssignWork = (): boolean => {
    // Admin can assign to anyone, Manager can assign to their team, but not to themselves
    if (selectedUserId === currentUser?.uid) return false;
    return isAdmin || isManager;
  };

  // Check if any data is still loading
  const isAnyDataLoading = (): boolean => {
    return Object.values(loadingStates).some(loading => loading);
  };

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div className="flow-root">
      <ul className="-mb-8">
        {[1, 2, 3].map((index) => (
          <li key={index}>
            <div className="relative pb-8">
              {index !== 3 && (
                <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
              )}
              <div className="relative flex items-start space-x-3">
                <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse ring-8 ring-white" />
                <div className="min-w-0 flex-1">
                  <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
                    </div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      {/* Sliding Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 h-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Resize Handle */}
        <div
          className="absolute left-0 top-0 w-1 h-full bg-gray-300 hover:bg-blue-500 cursor-ew-resize transition-colors group"
          onMouseDown={handleResizeStart}
        >
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gray-400 group-hover:bg-blue-600 transition-colors"></div>
        </div>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Calendar</h2>
            <div className="flex items-center space-x-2">
              {/* Refresh Button */}
              <button
                onClick={handleRefreshData}
                disabled={isRefreshing || isAnyDataLoading()}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh Calendar Data"
              >
                <svg
                  className={`w-5 h-5 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* User Selection (for managers and admins) */}
            {(isManager || isAdmin) && users.length > 1 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Select User
                </label>
                <div className="relative" ref={dropdownRef}>
                  {/* Custom Dropdown Button */}
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="w-full p-3 pl-4 pr-10 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer hover:border-gray-400 hover:shadow-sm text-left flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-medium text-sm">
                          {selectedUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{selectedUser?.name || 'Select User'}</div>
                        <div className="text-sm text-gray-500">{selectedUser?.email || ''}</div>
                      </div>
                    </div>
                    <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      {/* Search Input */}
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* User List */}
                      <div className="max-h-40 overflow-y-auto">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleUserSelect(user.id)}
                              className={`w-full p-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center ${selectedUserId === user.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                                }`}
                            >
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-gray-600 font-medium text-sm">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{user.name}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                              {selectedUserId === user.id && (
                                <svg className="ml-auto w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            No users found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Date Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Add Entry Form - Collapsible */}
            {(canAddEntry() || canAssignWork()) && (
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                {/* Header with toggle button */}
                <div
                  className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer"
                  onClick={() => setIsAddEntryExpanded(!isAddEntryExpanded)}
                >
                  <h3 className="text-lg font-medium text-gray-800">
                    {canAddEntry()
                      ? (entryMode === 'journal' ? 'Add Journal Entry' : 'Add Work Assignment')
                      : 'Assign Work'
                    }
                  </h3>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700"
                    aria-label={isAddEntryExpanded ? 'Collapse form' : 'Expand form'}
                  >
                    <svg
                      className={`w-5 h-5 transition-transform ${isAddEntryExpanded ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Collapsible content */}
                {isAddEntryExpanded && (
                  <div className="p-4">
                    {/* Toggle between Journal and Work Assignment (only for self) */}
                    {canAddEntry() && (
                      <div className="mb-4">
                        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                          <button
                            type="button"
                            onClick={() => setEntryMode('journal')}
                            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${entryMode === 'journal'
                              ? 'bg-white text-blue-600 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                              }`}
                          >
                            Journal Entry
                          </button>
                          <button
                            type="button"
                            onClick={() => setEntryMode('workAssignment')}
                            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${entryMode === 'workAssignment'
                              ? 'bg-white text-purple-600 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                              }`}
                          >
                            Work Assignment
                          </button>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {(entryMode === 'journal' && canAddEntry()) ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Journal Entry
                            </label>
                            <textarea
                              value={journal}
                              onChange={(e) => setJournal(e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows={3}
                              placeholder="What did you work on today?"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={startTime}
                                onChange={(e) => handleStartTimeChange(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                value={endTime}
                                onChange={(e) => handleEndTimeChange(e.target.value)}
                                min={startTime}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Work Assignment Description
                            </label>
                            <textarea
                              value={workDescription}
                              onChange={(e) => setWorkDescription(e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              rows={3}
                              placeholder={`Assign work to ${users.find(u => u.id === selectedUserId)?.name || 'selected user'}...`}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Due Time
                            </label>
                            <input
                              type="time"
                              value={workDueTime}
                              onChange={(e) => setWorkDueTime(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              required
                            />
                          </div>
                        </>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-2 px-4 rounded-md focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${(entryMode === 'journal' && canAddEntry())
                          ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                          : 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500'
                          }`}
                      >
                        {isLoading ? (
                          (entryMode === 'journal' && canAddEntry()) ? 'Adding Entry...' : 'Assigning Work...'
                        ) : (
                          (entryMode === 'journal' && canAddEntry()) ? 'Add Journal Entry' : 'Assign Work'
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Entries List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Entries for {new Date(selectedDate).toLocaleDateString()}
                </h3>
                {(isAnyDataLoading() || isRefreshing) && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{isRefreshing ? 'Refreshing...' : 'Loading entries...'}</span>
                  </div>
                )}
              </div>
              {/* count for the FollowUps , task , calls , journal , work Assigned */}
              <div className="mb-4 grid grid-cols-5 gap-3">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-700 font-medium">FollowUps</p>
                  <p className="text-2xl font-bold text-green-900">{followupEntries.filter(f => !f.isDone).length}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                  <p className="text-xs text-orange-700 font-medium">Tasks</p>
                  <p className="text-2xl font-bold text-orange-900">{taskEntries.filter(t => !t.isDone).length}</p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-3 border border-teal-200 cursor-pointer relative group">
                  <p className="text-xs text-teal-700 font-medium">Calls</p>
                  <p className="text-2xl font-bold text-teal-900">{callLogEntries.length}</p>

                  {/* Hover Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-sm rounded-lg px-4 py-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-40 shadow-lg">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center">
                        <span className="text-teal-300 font-medium mr-2">📞 Incoming:</span>
                        <span>{callLogEntries.filter(c => c.type === 'INCOMING').length}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-blue-300 font-medium mr-2">📤 Outgoing:</span>
                        <span>{callLogEntries.filter(c => c.type === 'OUTGOING').length}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-red-300 font-medium mr-2">❌ Missed:</span>
                        <span>{callLogEntries.filter(c => c.type === 'MISSED').length}</span>
                      </div>
                    </div>
                    {/* Tooltip arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                  <p className="text-xs text-purple-700 font-medium">Work Assigned</p>
                  <p className="text-2xl font-bold text-purple-900">{workAssignmentEntries.filter(w => !w.isDone).length}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium">Journals</p>
                  <p className="text-2xl font-bold text-blue-900">{journalEntries.length}</p>
                </div>
              </div>

              {(isAnyDataLoading() || isRefreshing) ? (
                <SkeletonLoader />
              ) : calendarItems.length > 0 ? (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {calendarItems.map((item, index) => (
                      <li key={item.id}>
                        <div className="relative pb-8">
                          {index !== calendarItems.length - 1 ? (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                          ) : null}
                          <div className="relative flex items-start space-x-3">
                            <div>
                              {(() => {
                                const getIcon = () => {
                                  switch (item.entryType) {
                                    case 'journal': return <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center ring-8 ring-white"><svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg></div>;
                                    case 'followup': return <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center ring-8 ring-white"><svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>;
                                    case 'task': return <div className="h-8 w-8 bg-orange-500 rounded-full flex items-center justify-center ring-8 ring-white"><svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg></div>;
                                    case 'workAssignment': return <div className="h-8 w-8 bg-purple-500 rounded-full flex items-center justify-center ring-8 ring-white"><svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.616V21a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h7.384m7.616 7.616a4.5 4.5 0 10-6.364-6.364 4.5 4.5 0 006.364 6.364z" /></svg></div>;
                                    case 'callLog': return <div className="h-8 w-8 bg-teal-500 rounded-full flex items-center justify-center ring-8 ring-white"><svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg></div>;
                                    default: return null;
                                  }
                                };
                                return getIcon();
                              })()}
                            </div>
                            <div className="min-w-0 flex-1">
                              {(() => {
                                if (item.entryType === 'journal') {
                                  const entry = item as CalendarEntry;
                                  const startTime = CalendarUtils.formatTime(entry.startTime);
                                  const endTime = CalendarUtils.formatTime(entry.endTime);
                                  return (
                                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center mb-1">
                                            <span className="text-xs font-semibold text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full mr-2">Journal</span>
                                            <span className="text-xs font-medium text-gray-700">{startTime} - {endTime}</span>
                                          </div>
                                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.journal}</p>
                                          <p className="text-xs text-gray-500 mt-1">By {entry.userName}</p>
                                        </div>
                                        {canEditEntry(entry.userId) && (
                                          <button onClick={() => handleDelete(entry.id, entry.userId, entry.journal)} className="ml-2 p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-full transition-colors" title="Delete entry">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                } else if (item.entryType === 'followup') {
                                  const followup = item as FollowUpEntry;
                                  const followupTime = followup.followUpTime.toDate();
                                  const timeString = format(followupTime, 'HH:mm');
                                  const isCompleted = followup.isDone;
                                  return (
                                    <div className={`bg-white p-3 rounded-lg shadow-sm border ${isCompleted ? 'border-gray-200 opacity-80' : 'border-green-300'}`}>
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center mb-1">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${isCompleted ? 'text-gray-700 bg-gray-100' : 'text-green-800 bg-green-100'}`}>{isCompleted ? '✓ Completed' : 'Follow-up'}</span>
                                            <span className={`text-xs font-medium ${isCompleted ? 'text-gray-500' : 'text-gray-700'}`}>{CalendarUtils.formatTime(timeString)}</span>
                                          </div>
                                          <p className={`text-sm ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{followup.followUp}</p>
                                          <div className="flex items-center mt-1 text-xs text-gray-600">
                                            <span className="font-medium">For:</span>
                                            <span className="ml-1.5">{followup.studentName || 'Unknown Lead'}</span>
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end ml-2 space-y-1.5 flex-shrink-0">
                                          {!isCompleted && canEditEntry(followup.userId) && (
                                            <button onClick={() => handleMarkAsDone(followup.id, followup.leadId, followup.followUp)} className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors w-full text-center" title="Mark as done">Mark as Done</button>
                                          )}
                                          <button onClick={() => navigate(`/studentdetails/${followup.leadId}`)} className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors w-full text-center" title="View Details">View Details</button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else if (item.entryType === 'task') {
                                  const task = item as TaskEntry;
                                  const taskTime = task.dueDate.toDate();
                                  const timeString = format(taskTime, 'HH:mm');
                                  const isCompleted = task.isDone;
                                  return (
                                    <div className={`bg-white p-3 rounded-lg shadow-sm border ${isCompleted ? 'border-gray-200 opacity-80' : 'border-orange-300'}`}>
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center mb-1">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${isCompleted ? 'text-gray-700 bg-gray-100' : 'text-orange-800 bg-orange-100'}`}>{isCompleted ? '✓ Task Done' : 'Task'}</span>
                                            <span className={`text-xs font-medium ${isCompleted ? 'text-gray-500' : 'text-gray-700'}`}>{CalendarUtils.formatTime(timeString)}</span>
                                          </div>
                                          <p className={`text-sm ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{task.task}</p>
                                          <div className="flex items-center mt-1 text-xs text-gray-600">
                                            <span className="font-medium">For:</span>
                                            <span className="ml-1.5">{task.leadName || 'Unknown Lead'}</span>
                                            {task.assignedByName && (<><span className="mx-1.5">•</span><span>Assigned by {task.assignedByName}</span></>)}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end ml-2 space-y-1.5 flex-shrink-0">
                                          {!isCompleted && canEditEntry(task.userId) && (
                                            <button onClick={() => handleMarkTaskAsDone(task.id, task.task)} className="px-3 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors w-full text-center" title="Mark as done">Mark as Done</button>
                                          )}
                                          <button onClick={() => navigate(`/studentdetails/${task.leadId}`)} className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors w-full text-center" title="View Details">View Details</button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else if (item.entryType === 'workAssignment') {
                                  const work = item as WorkAssignmentEntry;
                                  const workTime = work.dueDate.toDate();
                                  const timeString = format(workTime, 'HH:mm');
                                  const isCompleted = work.isDone;
                                  return (
                                    <div className={`bg-white p-3 rounded-lg shadow-sm border ${isCompleted ? 'border-gray-200 opacity-80' : 'border-purple-300'}`}>
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center mb-1">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${isCompleted ? 'text-gray-700 bg-gray-100' : 'text-purple-800 bg-purple-100'}`}>{isCompleted ? '✓ Work Done' : 'Work Assignment'}</span>
                                            <span className={`text-xs font-medium ${isCompleted ? 'text-gray-500' : 'text-gray-700'}`}>{CalendarUtils.formatTime(timeString)}</span>
                                          </div>
                                          <p className={`text-sm ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{work.workDescription}</p>
                                          <div className="flex items-center mt-1 text-xs text-gray-600">
                                            <span className="font-medium">Assigned by:</span>
                                            <span className="ml-1.5">{work.assignedByName || 'Unknown'}</span>
                                          </div>
                                        </div>
                                        {!isCompleted && canEditEntry(work.userId) && (
                                          <button onClick={() => handleMarkWorkAsDone(work.id, work.workDescription)} className="ml-2 px-3 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors flex-shrink-0" title="Mark as done">✓ Done</button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                } else if (item.entryType === 'callLog') {
                                  const callLog = item as CallLogEntry;
                                  const callTime = callLog.callTimestamp.toDate();
                                  const timeString = format(callTime, 'HH:mm');
                                  const callTypeInfo = CallLogService.getCallTypeDisplay(callLog.type);
                                  const durationText = CallLogService.formatDuration(callLog.duration);
                                  return (
                                    <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-300">
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center mb-1">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${callTypeInfo.color}`}>📞 {callTypeInfo.label}</span>
                                            <span className="text-xs font-medium text-gray-700">{format(callTime, 'hh:mm:ss a')}</span>
                                          </div>
                                          <div className="flex items-center mb-1 text-sm">
                                            <span className="text-gray-800 font-medium mr-2">Lead:</span>
                                            <span className="text-gray-600">{callLog.leadName || 'Unknown Lead'}</span>
                                          </div>
                                          <div className="flex items-center text-xs text-gray-600">
                                            <span className="font-medium mr-1.5">Duration:</span>
                                            <span>{durationText}</span>
                                            <span className="mx-2">•</span>
                                            <span className="font-medium mr-1">By:</span>
                                            <span>{callLog.userName || 'Unknown User'}</span>
                                          </div>
                                        </div>
                                        <div className="ml-2 flex-shrink-0">
                                          <button onClick={() => navigate(`/studentdetails/${callLog.leadUid}`)} className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors" title="View Details">View Details</button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500">No entries for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete Journal Entry</h3>
                  <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700 font-medium mb-1">Entry to be deleted:</p>
                <p className="text-sm text-gray-600 italic">"{deleteModal.entryJournal}"</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete Entry'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Done Confirmation Modal */}
      {markDoneModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Mark Follow-up as Done</h3>
                  <p className="text-sm text-gray-500 mt-1">This will mark the follow-up as completed.</p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700 font-medium mb-1">Follow-up to be completed:</p>
                <p className="text-sm text-gray-600 italic">"{markDoneModal.description}"</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelMarkAsDone}
                  disabled={isMarkingDone}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMarkAsDone}
                  disabled={isMarkingDone}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isMarkingDone ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Marking Done...
                    </>
                  ) : (
                    'Mark as Done'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Task as Done Confirmation Modal */}
      {markTaskDoneModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Mark Task as Done</h3>
                  <p className="text-sm text-gray-500 mt-1">This will mark the task as completed.</p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700 font-medium mb-1">Task to be completed:</p>
                <p className="text-sm text-gray-600 italic">"{markTaskDoneModal.description}"</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelMarkTaskAsDone}
                  disabled={isMarkingTaskDone}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMarkTaskAsDone}
                  disabled={isMarkingTaskDone}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isMarkingTaskDone ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Marking Done...
                    </>
                  ) : (
                    'Mark as Done'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Work Assignment as Done Confirmation Modal */}
      {markWorkDoneModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Mark Work Assignment as Done</h3>
                  <p className="text-sm text-gray-500 mt-1">This will mark the work assignment as completed.</p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700 font-medium mb-1">Work assignment to be completed:</p>
                <p className="text-sm text-gray-600 italic">"{markWorkDoneModal.description}"</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelMarkWorkAsDone}
                  disabled={isMarkingWorkDone}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMarkWorkAsDone}
                  disabled={isMarkingWorkDone}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isMarkingWorkDone ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Marking Done...
                    </>
                  ) : (
                    'Mark as Done'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Calendar;
